import React, { useCallback, useEffect, useRef, useState } from 'react';
import '@github/relative-time-element';
import { __ } from '@wordpress/i18n';

// components
import ActivityAvatar from '../ActivityAvatar';
import CuePreview from '../CuePreview';
import RichText from '../RichText';

// icons
import ArchiveIcon from '../../../../icons/ArchiveIcon';

// store
import { useCommCenter } from '../../context';

// constants
import { ACTIVITY_TYPES } from '../../constants';

// Horizontal drag distance, in px, that commits the swipe-to-archive gesture.
const ARCHIVE_THRESHOLD_PX = 72;
const MAX_DRAG_PX = 104;

/**
 * Names the event the card reports. The feed mixes tasks with comments, cue
 * edits and team events, and the coloured circle alone did not say which is
 * which — so every card states its kind in words.
 *
 * An activity given a name of its own is announced by that name instead: it was
 * written for this card.
 */
const kindLabel = (activity) => {
    if (activity.name) return activity.name;

    if (activity.sourceType === 'assigned_task') {
        if (activity.status === 'completed') return __('Attività completata', 'soundbetween');
        if (activity.status === 'in_review') return __('Attività da verificare', 'soundbetween');

        return __('Attività assegnata', 'soundbetween');
    }

    switch (activity.type) {
        case ACTIVITY_TYPES.COMMENTS:
            return __('Nuovo commento', 'soundbetween');
        case ACTIVITY_TYPES.CUE_COMPLETED:
            return __('Cue completata', 'soundbetween');
        case ACTIVITY_TYPES.CUE_UPDATED:
            return __('Cue aggiornata', 'soundbetween');
        case ACTIVITY_TYPES.TEAM_JOINED:
            return __('Nuovo membro del team', 'soundbetween');
        case ACTIVITY_TYPES.TEAM_INVITED:
            return __('Invito al team', 'soundbetween');
        case ACTIVITY_TYPES.DEADLINE:
            return __('Scadenza', 'soundbetween');
        default:
            return __('Attività', 'soundbetween');
    }
}

const ActivityCard = ({ activity, archivedView }) => {
    const {
        markActivityRead,
        markActivityUnread,
        archiveActivity,
        unarchiveActivity,
        approveActivity,
        submitActivityForReview,
        draftCommentFromActivity,
        openCommentThread,
        highlightActivityId,
        isProjectScope,
        showArchived,
        can,
        pending,
    } = useCommCenter();

    const isArchivedView = archivedView ?? showArchived;
    // Jumped to from the comment it belongs to: bring it into view and mark it.
    const isHighlighted = highlightActivityId === activity.id;
    const itemRef = useRef(null);

    useEffect(() => {
        if (isHighlighted) itemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [isHighlighted]);

    // The API exposes no action list on an activity: what can be done follows
    // from its status and from the role of whoever is looking at it, which is
    // exactly what the policy answers (helpers/projectRoles.js).
    const canApprove = can.approveActivityNow(activity);
    const canSubmitReview = can.submitForReview(activity);
    const canComment = can.comment && activity.status !== 'completed';
    // Every activity born from a message can point back at it, whatever the role
    // — but only where a Communications tab exists to jump to: the cross project
    // feed of the Projects page has none, and its messages belong elsewhere.
    const hasSourceComment = isProjectScope && Boolean(activity.sourceMessageId);
    const hasActions = canApprove || canSubmitReview || canComment || hasSourceComment;

    const [dragX, setDragX] = useState(0);
    const [dragging, setDragging] = useState(false);
    const startXRef = useRef(0);
    const pointerIdRef = useRef(null);

    const onPointerDown = useCallback((e) => {
        if (activity.unread) markActivityRead(activity.id);
        if (isArchivedView || (e.pointerType === 'mouse' && e.button !== 0)) return;
        // Never start a swipe from an interactive element inside the card.
        if (e.target.closest('button, a, [role="slider"]')) return;

        pointerIdRef.current = e.pointerId;
        startXRef.current = e.clientX;
        setDragging(true);
    }, [activity.id, activity.unread, isArchivedView, markActivityRead]);

    const onPointerMove = useCallback((e) => {
        if (!dragging || pointerIdRef.current !== e.pointerId) return;

        const delta = e.clientX - startXRef.current;
        // Only a left swipe archives; a right drag is clamped to zero.
        setDragX(Math.max(-MAX_DRAG_PX, Math.min(0, delta)));
    }, [dragging]);

    const endDrag = useCallback(() => {
        if (!dragging) return;

        setDragging(false);

        if (dragX <= -ARCHIVE_THRESHOLD_PX) {
            archiveActivity(activity.id);
        }

        setDragX(0);
        pointerIdRef.current = null;
    }, [activity.id, archiveActivity, dragX, dragging]);

    const swipeProgress = Math.min(1, Math.abs(dragX) / ARCHIVE_THRESHOLD_PX);

    return (
        <li
            ref={itemRef}
            className={`activityItem ${activity.unread ? 'activityItem--unread' : ''} ${isHighlighted ? 'is-highlighted' : ''}`}>
            <span
                className="activityItem__swipeHint"
                style={{ opacity: swipeProgress }}
                aria-hidden="true">
                <ArchiveIcon size={16}/>
            </span>
            <article
                className="activityCard"
                style={{
                    transform: dragX ? `translateX(${dragX}px)` : undefined,
                    transition: dragging ? 'none' : undefined,
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onPointerLeave={endDrag}>
                <ActivityAvatar
                    type={activity.type}
                    actor={activity.actor}
                    showIcon={activity.showIcon}/>

                <div className="activityCard__content">
                    <p className={`activityCard__kind activityCard__kind--${activity.type}`}>
                        {kindLabel(activity)}
                    </p>

                    <p className="activityCard__text">
                        <RichText segments={activity.body}/>
                    </p>

                    {activity.cue && <CuePreview cue={activity.cue}/>}

                    {hasActions && !isArchivedView && (
                        <div className="activityCard__actions">
                            {canSubmitReview && (
                                <button
                                    type="button"
                                    className="commButton commButton--accent"
                                    disabled={pending?.review}
                                    onClick={() => submitActivityForReview(activity.id)}
                                    title={__('Segnala il lavoro come pronto da verificare', 'soundbetween')}>
                                    {__('Invia in revisione', 'soundbetween')}
                                </button>
                            )}
                            {canApprove && (
                                <button
                                    type="button"
                                    className={`commButton ${canSubmitReview ? 'commButton--ghost' : 'commButton--accent'}`}
                                    disabled={pending?.approve}
                                    onClick={() => approveActivity(activity.id)}
                                    title={__('Segna l’attività come completata', 'soundbetween')}>
                                    {__('Approva', 'soundbetween')}
                                </button>
                            )}
                            {canComment && (
                                <button
                                    type="button"
                                    className="commButton commButton--ghost"
                                    onClick={() => draftCommentFromActivity(activity)}
                                    title={__('Crea una bozza di commento', 'soundbetween')}>
                                    {__('Commenta', 'soundbetween')}
                                </button>
                            )}
                            {hasSourceComment && (
                                <button
                                    type="button"
                                    className="commButton commButton--quiet"
                                    onClick={() => openCommentThread(activity.sourceMessageId)}
                                    title={__('Apri il commento da cui nasce questa attività', 'soundbetween')}>
                                    {__('Vedi il commento', 'soundbetween')}
                                </button>
                            )}
                        </div>
                    )}

                    <div className="activityCard__footer">
                        <relative-time class="activityCard__time" datetime={activity.createdAt}/>
                        {isArchivedView
                            ? (
                                <button
                                    type="button"
                                    className="activityCard__restore"
                                    onClick={() => unarchiveActivity(activity.id)}>
                                    {__('Ripristina', 'soundbetween')}
                                </button>
                            )
                            : (
                                <button
                                    type="button"
                                    className="activityCard__archive"
                                    onClick={() => archiveActivity(activity.id)}
                                    title={__('Archivia attività', 'soundbetween')}>
                                    <ArchiveIcon size={14}/>
                                </button>
                            )}
                    </div>
                </div>

                <button
                    type="button"
                    className={`activityCard__unread ${activity.unread ? 'is-unread' : ''}`}
                    onClick={() => (activity.unread
                        ? markActivityRead(activity.id)
                        : markActivityUnread(activity.id))}
                    title={activity.unread
                        ? __('Segna come letta', 'soundbetween')
                        : __('Segna come da leggere', 'soundbetween')}
                    aria-label={activity.unread
                        ? __('Non letta', 'soundbetween')
                        : __('Letta', 'soundbetween')}/>
            </article>
        </li>
    )
}

export default ActivityCard;
